import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../../../atoms/alert/alert.service';
import { CurrentUserService } from '../../../common/current-user/current-user.service';
import { FirebaseService } from '../../../common/firebase/firebase.service';
import { CreateAgencyDto } from '../../../common/general-types/agency.dto';
import { CreateBusinessAccountDto, CreateUserDto, UserRolesEnum } from '../../../common/general-types/users.dto';
import { GaService } from '../../../services/ga/ga';
import { mirrorBrandImages, S3uploaderService } from '../../../services/s3uploader/s3uploader.service';
import { ONBOARDING_FLOW, onboardingStepIndex } from '../../../services/ga/onboarding.analytics';
import { BrandFetchService, BrandSuggestionResponse } from '../../onboarding/service/brand-fetch/brand-fetch.service';
import { isBlockedEmailDomain } from '../../onboarding-v2/service/slick-onboard/slick-onboard.service';
import {
  CampaignCurrency,
  DisplayCurrency,
  ValueFirstBrand,
  accountFieldError,
  brandFromBrandfetch,
  brandFromSuggestion,
  detectCampaignCurrency,
  isValidWorkEmail,
  normalizeDomain,
  splitPersonName,
} from '../../onboarding-value-first/services/value-first-onboarding.types';
import {
  AGENCY_BRAND_CAP,
  AGENCY_STORAGE_KEY,
  AGENCY_VALUE_FIRST_VARIANT,
  AgencyCreatedBrand,
  AgencyDraft,
  AgencyStep,
  AgencyRepresentedBrand,
  agencyPreviousStep,
  agencyProgress,
  agencyStepAfterRefresh,
  agencyWebsiteError,
  emptyAgencyDraft,
  readAgencyDraft,
  representedBrandError,
} from './value-first-agency.types';
import { AdConversionsService } from '@/app/services/analytics/ad-conversions.service';

export type RegistrationResult = 'ok' | 'exists' | 'error' | 'need-name';

interface GoogleUser {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ValueFirstAgencyService {
  readonly draft = signal<AgencyDraft>(emptyAgencyDraft());
  readonly error = signal('');
  readonly finding = signal(false);
  readonly sendingCode = signal(false);
  readonly verifying = signal(false);
  readonly registering = signal(false);
  readonly resendCooldown = signal(0);
  readonly displayCurrency = signal<DisplayCurrency | null>(null);
  readonly turnstileToken = signal<string | null>(null);
  readonly verificationToken = signal<string | null>(null);
  readonly googleUid = signal<string | null>(null);
  readonly googlePhotoUrl = signal('');
  readonly password = signal('');

  readonly step = computed(() => this.draft().step);
  readonly progress = computed(() => agencyProgress(this.draft().step));
  readonly showBack = computed(() => this.draft().step !== 'welcome');
  readonly agencyName = computed(() => this.draft().agency?.name || this.fallbackName(this.draft().url));
  readonly atBrandCap = computed(() => this.draft().brands.length >= AGENCY_BRAND_CAP);
  readonly activeCurrency = computed<CampaignCurrency>(() => {
    const chosen = this.draft().currency;
    if (chosen) {
      return chosen;
    }
    const agency = this.draft().agency;
    const domain = agency?.domain || normalizeDomain(this.draft().url);
    return detectCampaignCurrency(domain, agency?.countryCode).currency;
  });

  private cooldownTimer: ReturnType<typeof setInterval> | null = null;
  private brandLookupSerial = 0;

  private readonly http = inject(HttpClient);
  private readonly adConversions = inject(AdConversionsService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly brandFetch = inject(BrandFetchService);
  private readonly firebase = inject(FirebaseService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly ga = inject(GaService);
  private readonly alert = inject(AlertService);
  private readonly uploader = inject(S3uploaderService);

  setDisplayCurrency(currency: DisplayCurrency): void {
    this.displayCurrency.set(currency);
  }

  setTurnstileToken(token: string | null): void {
    this.turnstileToken.set(token);
  }

  clearError(): void {
    this.error.set('');
  }

  restore(): void {
    this.password.set('');
    this.verificationToken.set(null);
    this.googleUid.set(null);
    this.googlePhotoUrl.set('');
    this.clearCooldown();
    const stored = readAgencyDraft(this.sessionStorage());
    if (!stored) {
      this.commit(emptyAgencyDraft(), false);
    } else {
      this.commit({ ...stored, step: agencyStepAfterRefresh(stored) }, false);
    }
    this.trackStepView(this.draft().step);
  }

  goBack(): void {
    const next = agencyPreviousStep(this.draft());
    this.error.set('');
    if (!next) {
      void this.router.navigate(['/onboarding-select']);
      return;
    }
    this.commit({ ...this.draft(), step: next });
  }

  setUrl(url: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), url }, false);
  }

  setBrandUrl(brandUrl: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), brandUrl }, false);
  }

  setEmail(email: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), email }, false);
  }

  setName(firstName: string, lastName: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), firstName, lastName }, false);
  }

  setPassword(password: string): void {
    this.error.set('');
    this.password.set(password);
  }

  setCurrency(currency: CampaignCurrency): void {
    if (this.draft().currency !== currency) {
      this.track('currency_changed', {
        variant: AGENCY_VALUE_FIRST_VARIANT,
        currency,
      });
    }
    this.commit({ ...this.draft(), currency }, false);
  }

  selectPick(index: number): void {
    this.commit({ ...this.draft(), pick: index }, false);
  }

  openManual(): void {
    this.track('manual_fallback', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      reason: 'no_website',
    });
    this.error.set('');
    this.commit({ ...this.draft(), fromLookup: false, step: 'manual' });
  }

  editAgency(): void {
    this.error.set('');
    this.commit({ ...this.draft(), step: 'manual' });
  }

  rejectAgency(): void {
    this.track('not_my_agency', { variant: AGENCY_VALUE_FIRST_VARIANT });
    this.error.set('');
    this.commit({
      ...this.draft(),
      url: '',
      agency: null,
      fromLookup: false,
      currency: null,
      step: 'website',
    });
  }

  submitManual(name: string): void {
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      this.error.set('Enter your agency name.');
      return;
    }
    const domain = normalizeDomain(this.draft().url);
    const currency = this.draft().currency ?? detectCampaignCurrency(domain, this.draft().agency?.countryCode).currency;
    const agency: ValueFirstBrand = {
      name: trimmedName,
      domain,
      industry: '',
      description: this.draft().agency?.description ?? '',
      logoUrl: this.draft().agency?.logoUrl,
      bannerUrl: this.draft().agency?.bannerUrl,
      countryCode: this.draft().agency?.countryCode,
      socials: this.draft().agency?.socials ?? [],
    };
    this.error.set('');
    this.commit({ ...this.draft(), agency, currency, step: 'brands' });
  }

  continueToBrands(): void {
    this.error.set('');
    this.commit({ ...this.draft(), currency: this.activeCurrency(), step: 'brands' });
  }

  async findAgency(): Promise<void> {
    const message = agencyWebsiteError(this.draft().url);
    const domain = normalizeDomain(this.draft().url);
    if (message) {
      this.error.set(message);
      return;
    }
    this.finding.set(true);
    this.error.set('');
    this.commit({ ...this.draft(), url: domain }, false);
    try {
      const suggestions = await firstValueFrom(
        this.brandFetch.brandSearch(domain).pipe(catchError(() => of([] as BrandSuggestionResponse[]))),
      );
      const best = this.bestSuggestion(suggestions);
      if (!best) {
        this.failLookup(domain);
        return;
      }
      await this.applySuggestion(best, domain);
    } catch {
      this.failLookup(domain);
    } finally {
      this.finding.set(false);
    }
  }

  async selectSuggestion(suggestion: BrandSuggestionResponse): Promise<void> {
    const domain = normalizeDomain(suggestion.domain);
    if (!domain || !domain.includes('.')) {
      this.error.set('Enter a full website, like youragency.com.');
      return;
    }
    this.finding.set(true);
    this.error.set('');
    this.commit({ ...this.draft(), url: domain }, false);
    try {
      await this.applySuggestion(suggestion, domain);
    } catch {
      this.failLookup(domain);
    } finally {
      this.finding.set(false);
    }
  }

  async addBrand(): Promise<void> {
    const message = representedBrandError(this.draft().brandUrl);
    const domain = normalizeDomain(this.draft().brandUrl);
    if (message) {
      this.error.set(message);
      return;
    }
    if (this.rejectBrandSlot(domain)) {
      return;
    }
    const id = this.nextBrandId(domain);
    const pending: AgencyRepresentedBrand = {
      id,
      name: this.fallbackName(domain),
      domain,
      status: 'loading',
    };
    this.error.set('');
    this.commit({ ...this.draft(), brandUrl: '', brands: [...this.draft().brands, pending] }, false);
    await this.resolveBrand(id, domain);
  }

  async addBrandFromSuggestion(suggestion: BrandSuggestionResponse): Promise<void> {
    const domain = normalizeDomain(suggestion.domain);
    if (!domain || !domain.includes('.')) {
      this.error.set("Enter the brand's website, like brand.com.");
      return;
    }
    if (this.rejectBrandSlot(domain)) {
      return;
    }
    const id = this.nextBrandId(domain);
    const pending: AgencyRepresentedBrand = {
      id,
      name: suggestion.name.trim() || this.fallbackName(domain),
      domain,
      logoUrl: suggestion.icon || undefined,
      status: 'loading',
    };
    this.error.set('');
    this.commit({ ...this.draft(), brandUrl: '', brands: [...this.draft().brands, pending] }, false);
    try {
      await this.applyFoundBrand(id, suggestion, domain);
    } catch {
      this.markBrandFailed(id, domain);
    }
  }

  addBrandManually(name: string, website: string): boolean {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      this.error.set('Enter the brand name.');
      return false;
    }
    const rawWebsite = website.trim();
    let domain = '';
    if (rawWebsite) {
      const message = representedBrandError(rawWebsite);
      if (message) {
        this.error.set(message);
        return false;
      }
      domain = normalizeDomain(rawWebsite);
    }
    if (domain) {
      if (this.rejectBrandSlot(domain)) {
        return false;
      }
    } else if (this.draft().brands.some((brand) => !brand.domain && brand.name.trim().toLowerCase() === trimmed.toLowerCase())) {
      this.error.set('This brand is already on your list.');
      return false;
    } else if (this.draft().brands.length >= AGENCY_BRAND_CAP) {
      this.error.set('Your plan includes 3 brands. You can add more from the dashboard.');
      return false;
    }
    const brand: AgencyRepresentedBrand = {
      id: this.nextBrandId(domain || 'manual'),
      name: trimmed,
      domain,
      status: 'ready',
    };
    this.error.set('');
    this.track('brand_added', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain: domain || 'manual',
      result: 'ready',
    });
    this.commit({ ...this.draft(), brandUrl: '', brands: [...this.draft().brands, brand] }, false);
    return true;
  }

  removeBrand(id: string): void {
    this.track('brand_removed', { variant: AGENCY_VALUE_FIRST_VARIANT });
    this.commit({
      ...this.draft(),
      brands: this.draft().brands.filter((brand) => brand.id !== id),
    }, false);
  }

  continueWithBrands(): void {
    if (this.draft().brands.some((brand) => brand.status === 'loading')) {
      this.error.set('Still finding a brand. Wait a moment, or remove it.');
      return;
    }
    if (this.draft().brands.length === 0) {
      this.error.set('Add at least one brand, or add them later.');
      return;
    }
    this.track('brand_count_at_continue', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      count: this.draft().brands.length,
    });
    this.error.set('');
    this.commit({ ...this.draft(), step: 'account' });
  }

  skipBrands(): void {
    if (this.draft().brands.some((brand) => brand.status === 'loading')) {
      this.error.set('Still finding a brand. Wait a moment, or remove it.');
      return;
    }
    this.track('brands_skipped', { variant: AGENCY_VALUE_FIRST_VARIANT });
    this.error.set('');
    this.commit({ ...this.draft(), step: 'account' });
  }

  async sendCode(honeypot: string, resend = false): Promise<boolean> {
    const email = this.draft().email.trim();
    if (honeypot.trim()) {
      return false;
    }
    if (!resend) {
      const fieldError = accountFieldError({
        firstName: this.draft().firstName,
        lastName: this.draft().lastName,
        password: this.password(),
        requirePassword: true,
      });
      if (fieldError) {
        this.error.set(fieldError);
        return false;
      }
    }
    if (!isValidWorkEmail(email)) {
      this.error.set('Enter a valid work email address.');
      return false;
    }
    if (isBlockedEmailDomain(email)) {
      this.error.set('Use your work email address to continue.');
      return false;
    }
    if (this.resendCooldown() > 0 || this.sendingCode()) {
      return false;
    }
    this.sendingCode.set(true);
    this.error.set('');
    try {
      await firstValueFrom(
        this.http.post(`${environment.shout_api.url}/auth/send-email-otp`, {
          email,
          turnstileToken: this.turnstileToken() ?? undefined,
          honeypot: honeypot || undefined,
        }),
      );
      this.commit({ ...this.draft(), email, authMode: 'email', step: 'code' });
      this.track(resend ? 'code_resend' : 'code_sent', {
        variant: AGENCY_VALUE_FIRST_VARIANT,
      });
      this.startCooldown();
      return true;
    } catch (error) {
      this.error.set(this.readErrorMessage(error, 'Failed to send code'));
      return false;
    } finally {
      this.sendingCode.set(false);
    }
  }

  async verifyCode(code: string): Promise<boolean> {
    const digits = code.replace(/\D/g, '').slice(0, 6);
    if (digits.length !== 6 || this.verifying()) {
      return false;
    }
    if (!this.password()) {
      this.error.set('Enter your password again before we create the agency.');
      this.commit({ ...this.draft(), step: 'account' });
      return false;
    }
    this.verifying.set(true);
    this.error.set('');
    let outcome: RegistrationResult = 'error';
    try {
      const result = await firstValueFrom(
        this.http.post<{ verificationToken: string }>(`${environment.shout_api.url}/auth/verify-email-otp`, {
          email: this.draft().email.trim(),
          otp: digits,
        }),
      );
      this.verificationToken.set(result.verificationToken);
      this.track('code_verified', { variant: AGENCY_VALUE_FIRST_VARIANT });
      outcome = await this.createAccount({
        firebaseUid: '',
        email: this.draft().email.trim(),
        firstName: this.draft().firstName.trim(),
        lastName: this.draft().lastName.trim(),
        photoURL: '',
        password: this.password(),
      });
    } catch (error) {
      this.verifying.set(false);
      this.error.set(this.readErrorMessage(error, 'Invalid code'));
      return false;
    }
    this.verifying.set(false);
    await this.finishRegistration(outcome, this.error());
    return outcome === 'ok';
  }

  async continueWithGoogle(): Promise<void> {
    this.track('sso_click', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      provider: 'google',
    });
    this.registering.set(true);
    this.error.set('');
    this.firebase.beginDeferPostLoginBootstrap();
    let outcome: RegistrationResult = 'error';
    let message = '';
    try {
      const result = await firstValueFrom(this.firebase.signInWithGoogle());
      const user = (result as { user?: GoogleUser }).user;
      if (!user?.uid || !user.email) {
        message = 'Google did not return an email address.';
        outcome = 'error';
      } else {
        const names = splitPersonName(user.displayName);
        this.googleUid.set(user.uid);
        this.googlePhotoUrl.set(user.photoURL ?? '');
        this.commit({
          ...this.draft(),
          email: user.email,
          firstName: names.firstName || this.draft().firstName,
          lastName: names.lastName || this.draft().lastName,
          authMode: 'google',
        }, false);
        if (!names.firstName || !names.lastName) {
          outcome = 'need-name';
        } else {
          outcome = await this.createAccount({
            firebaseUid: user.uid,
            email: user.email,
            firstName: names.firstName,
            lastName: names.lastName,
            photoURL: user.photoURL ?? '',
            password: null,
          });
          message = this.error();
        }
      }
    } catch (error) {
      outcome = this.isExistingAccount(error) ? 'exists' : 'error';
      message = this.readErrorMessage(error, 'Google sign-in failed.');
    } finally {
      this.firebase.endDeferPostLoginBootstrap();
      this.registering.set(false);
    }
    await this.finishRegistration(outcome, message);
  }

  async completeGoogleAccount(): Promise<void> {
    const fieldError = accountFieldError({
      firstName: this.draft().firstName,
      lastName: this.draft().lastName,
      password: '',
      requirePassword: false,
    });
    if (fieldError) {
      this.error.set(fieldError);
      return;
    }
    const uid = this.googleUid();
    if (!uid) {
      this.error.set('Google sign-in expired. Continue with Google again.');
      this.commit({ ...this.draft(), authMode: 'email' }, false);
      return;
    }
    this.registering.set(true);
    this.error.set('');
    this.firebase.beginDeferPostLoginBootstrap();
    let outcome: RegistrationResult = 'error';
    try {
      outcome = await this.createAccount({
        firebaseUid: uid,
        email: this.draft().email.trim(),
        firstName: this.draft().firstName.trim(),
        lastName: this.draft().lastName.trim(),
        photoURL: this.googlePhotoUrl(),
        password: null,
      });
    } catch (error) {
      outcome = this.isExistingAccount(error) ? 'exists' : 'error';
      if (outcome === 'error' && !this.error()) {
        this.error.set(this.readErrorMessage(error, 'We could not finish creating your agency. Please try again.'));
      }
    } finally {
      this.firebase.endDeferPostLoginBootstrap();
      this.registering.set(false);
    }
    await this.finishRegistration(outcome, this.error());
  }

  async finish(choice: 'start' | 'skip'): Promise<void> {
    if (choice === 'start') {
      const chosen = this.draft().createdBrands[this.draft().pick];
      if (chosen) {
        this.currentUser.setFocussedBrand(this.focusPayload(chosen), this.firebase.accessToken);
      }
      this.track('first_brief_brand_selected', {
        variant: AGENCY_VALUE_FIRST_VARIANT,
        brand: chosen?.domain ?? '',
      });
    } else {
      this.track('skip_to_dashboard', { variant: AGENCY_VALUE_FIRST_VARIANT });
    }
    this.currentUser.onboardingComplete.set(true);
    this.currentUser.checkBrandComplete();
    this.clearSession();
    await this.router.navigate(['/dashboard']);
  }

  private async finishRegistration(outcome: RegistrationResult, message: string): Promise<void> {
    if (outcome === 'ok') {
      await this.firebase.syncClaimsAndRefreshToken();
      if (this.draft().authMode === 'google') {
        await this.firebase.reloadCurrentUser();
      }
      this.firebase.runPostLoginBootstrapForCurrentUser();
      this.password.set('');
      this.error.set('');
      this.commit({ ...this.draft(), step: 'welcome', pick: 0 });
      return;
    }
    if (outcome === 'need-name') {
      this.error.set('');
      this.commit({ ...this.draft(), authMode: 'google', step: 'account' });
      return;
    }
    if (outcome === 'exists') {
      const login = await this.alert.alert({
        title: 'Login?',
        message: 'Looks like you already have an account with this email address. Would you like to login?',
        icon: 'info',
        confirmText: 'Login',
        cancelText: 'Cancel',
        type: 'confirm',
      });
      if (login) {
        await this.router.navigate(['/login']);
      }
      return;
    }
    await this.alert.alert({
      title: 'Something went wrong',
      message: message || 'We could not finish creating your agency. Please try again.',
      icon: 'error',
      type: 'alert',
      confirmText: 'Okay',
      cancelText: 'Close',
    });
  }

  private async createAccount(input: {
    firebaseUid: string;
    email: string;
    firstName: string;
    lastName: string;
    photoURL: string;
    password: string | null;
  }): Promise<RegistrationResult> {
    try {
      let firebaseUid = input.firebaseUid;
      if (!firebaseUid) {
        if (!input.password) {
          this.error.set('Enter a password to continue.');
          return 'error';
        }
        const credential = await firstValueFrom(this.firebase.signUp(input.email, input.password));
        firebaseUid = credential.user?.uid ?? '';
        if (!firebaseUid) {
          this.error.set('We could not create your login.');
          return 'error';
        }
      }
      const personalInfo: CreateUserDto = {
        firebaseUid,
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        role: UserRolesEnum['Brand Admin'],
        jobPosition: 'unknown',
        photoURL: input.photoURL,
        isActive: true,
        hubspotUserToken: this.hubspotToken(),
        verificationToken: input.password ? (this.verificationToken() ?? '') : undefined,
      };
      const createUserResult = await firstValueFrom(
        this.http.post(`${environment.shout_api.url}/business-accounts/onboarding-brand`, personalInfo),
      );
      if (!createUserResult) {
        this.error.set('We could not create your account.');
        return 'error';
      }
      this.currentUser.setShoutUser(createUserResult);

      const agency = this.draft().agency;
      const agencyBody: CreateAgencyDto = {
        name: agency?.name || this.agencyName() || 'No name...',
        website: agency?.domain || normalizeDomain(this.draft().url) || 'No website...',
        about: agency?.description || '',
        currencyCode: this.activeCurrency().toLowerCase(),
      };
      const associated = await firstValueFrom(
        this.http.post<{ user?: unknown; agency?: unknown }>(
          `${environment.shout_api.url}/registration/associate-agency-to-user-nested`,
          { agency: agencyBody, userId: firebaseUid },
        ),
      );
      const agencyRecord = this.readAgencyRecord(associated);
      if (!agencyRecord?.id) {
        this.error.set('We could not create your agency.');
        return 'error';
      }
      if (associated?.user) {
        this.currentUser.setShoutUser(associated.user);
      }
      this.currentUser.setFocussedAgency(this.agencyFocus(associated) ?? agencyRecord);
      void this.adConversions.saveFirstTouch('agency', agencyRecord.id);
      this.adConversions.trackSignup('agency', agencyRecord.id);

      const createdBrands: AgencyCreatedBrand[] = [];
      for (const brand of this.draft().brands) {
        const images = await mirrorBrandImages(this.uploader, brand.logoUrl, brand.bannerUrl);
        const businessInfo: CreateBusinessAccountDto = {
          companyName: brand.name || brand.domain,
          website: brand.domain || 'No website...',
          instagram: '',
          tiktok: '',
          about: brand.description || '',
          coverPhoto: images.coverPhoto,
          companyLogo: images.companyLogo,
          creatorId: firebaseUid,
          isActive: true,
          currencyCode: this.activeCurrency().toLowerCase(),
          isPayg: false,
          industries: [],
          vatNumber: '',
          agencyId: agencyRecord.id,
          stripeCustomerId: agencyRecord.stripeCustomerId || '',
        };
        const created = await firstValueFrom(
          this.http.post<{ business?: { id?: string; companyName?: string } }>(
            `${environment.shout_api.url}/registration/add-brand-to-agency-onboard`,
            businessInfo,
          ),
        );
        const businessId = created?.business?.id;
        if (!businessId) {
          continue;
        }
        createdBrands.push({
          id: businessId,
          name: created.business?.companyName || brand.name,
          domain: brand.domain,
          logoUrl: images.companyLogo || undefined,
        });
      }
      if (createdBrands[0]) {
        this.currentUser.setFocussedBrand(this.focusPayload(createdBrands[0]), this.firebase.accessToken);
      }
      this.commit({ ...this.draft(), createdBrands, pick: 0 }, false);
      this.track('agency_account_created', {
        variant: AGENCY_VALUE_FIRST_VARIANT,
        firebase_uid: firebaseUid,
        brand_count: createdBrands.length,
      });
      return 'ok';
    } catch (error) {
      if (this.isExistingAccount(error)) {
        return 'exists';
      }
      this.error.set(this.readErrorMessage(error, 'We could not finish creating your agency. Please try again.'));
      return 'error';
    }
  }

  private async resolveBrand(id: string, domain: string): Promise<void> {
    try {
      const suggestions = await firstValueFrom(
        this.brandFetch.brandSearch(domain).pipe(catchError(() => of([] as BrandSuggestionResponse[]))),
      );
      const best = this.bestSuggestion(suggestions);
      if (!best) {
        this.markBrandFailed(id, domain);
        return;
      }
      await this.applyFoundBrand(id, best, domain);
    } catch {
      this.markBrandFailed(id, domain);
    }
  }

  private async applyFoundBrand(id: string, suggestion: BrandSuggestionResponse, domain: string): Promise<void> {
    let brand = brandFromSuggestion(suggestion);
    if (suggestion.brandId) {
      try {
        const detail = await firstValueFrom(this.brandFetch.getBrandDetails(suggestion.brandId));
        brand = brandFromBrandfetch(detail, brand);
      } catch {
        // The search hit is enough to keep the row.
      }
    }
    this.markBrand(id, {
      name: brand.name || this.fallbackName(domain),
      domain: brand.domain || domain,
      logoUrl: suggestion.icon || brand.logoUrl,
      bannerUrl: brand.bannerUrl,
      description: brand.description,
      status: 'ready',
    });
    this.track('brand_added', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain,
      result: 'ready',
    });
  }

  private rejectBrandSlot(domain: string): boolean {
    if (domain && this.draft().brands.some((brand) => brand.domain === domain)) {
      this.error.set('This brand is already on your list.');
      return true;
    }
    if (this.draft().brands.length >= AGENCY_BRAND_CAP) {
      this.error.set('Your plan includes 3 brands. You can add more from the dashboard.');
      return true;
    }
    return false;
  }

  private nextBrandId(domain: string): string {
    const id = `${domain}-${this.brandLookupSerial}`;
    this.brandLookupSerial += 1;
    return id;
  }

  private markBrandFailed(id: string, domain: string): void {
    this.markBrand(id, { status: 'failed' });
    this.track('brand_added', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain,
      result: 'failed',
    });
  }

  private markBrand(id: string, patch: Partial<AgencyRepresentedBrand>): void {
    this.commit({
      ...this.draft(),
      brands: this.draft().brands.map((brand) => brand.id === id ? { ...brand, ...patch } : brand),
    }, false);
  }

  private async applySuggestion(suggestion: BrandSuggestionResponse, domain: string): Promise<void> {
    let agency = brandFromSuggestion(suggestion);
    if (suggestion.brandId) {
      try {
        const detail = await firstValueFrom(this.brandFetch.getBrandDetails(suggestion.brandId));
        agency = brandFromBrandfetch(detail, agency);
      } catch {
        // Search hit is enough to show the workspace.
      }
    }
    const currency = this.draft().currency ?? detectCampaignCurrency(agency.domain || domain, agency.countryCode).currency;
    this.track('agency_lookup_success', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain,
    });
    this.commit({ ...this.draft(), agency, currency, fromLookup: true, step: 'found' });
  }

  private failLookup(domain: string): void {
    this.track('agency_lookup_fail', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain,
    });
    this.track('manual_fallback', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      domain,
    });
    this.error.set("We couldn't find that agency. Add the details below.");
    this.commit({ ...this.draft(), fromLookup: false, step: 'manual' });
  }

  private bestSuggestion(suggestions: BrandSuggestionResponse[] | null | undefined): BrandSuggestionResponse | null {
    if (!suggestions || suggestions.length === 0) {
      return null;
    }
    return [...suggestions].sort((left, right) => (right._score ?? 0) - (left._score ?? 0))[0];
  }

  private fallbackName(domain: string): string {
    const label = normalizeDomain(domain).split('.')[0] ?? '';
    if (!label) {
      return '';
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private focusPayload(brand: AgencyCreatedBrand): { id: string; companyName: string; website: string; companyLogo: string } {
    return {
      id: brand.id,
      companyName: brand.name,
      website: brand.domain || 'No website...',
      companyLogo: brand.logoUrl ?? '',
    };
  }

  private agencyFocus(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const nested = (response as { agency?: unknown }).agency;
    if (nested && typeof nested === 'object') {
      return nested;
    }
    return response;
  }

  private readAgencyRecord(response: unknown): { id: string; stripeCustomerId: string } | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const record = response as { agency?: unknown; id?: unknown; stripeCustomerId?: unknown };
    const nested = record.agency && typeof record.agency === 'object' ? record.agency as { id?: unknown; stripeCustomerId?: unknown } : null;
    const source = nested?.id ? nested : record;
    if (typeof source.id !== 'string' || !source.id) {
      return null;
    }
    return {
      id: source.id,
      stripeCustomerId: typeof source.stripeCustomerId === 'string' ? source.stripeCustomerId : '',
    };
  }

  private commit(next: AgencyDraft, trackStep = true): void {
    const previous = this.draft().step;
    this.draft.set(next);
    this.writeSession(next);
    if (trackStep && next.step !== previous) {
      this.trackStepView(next.step);
    }
  }

  private trackStepView(step: AgencyStep): void {
    this.track('onboarding_step_view', {
      variant: AGENCY_VALUE_FIRST_VARIANT,
      step,
      step_index: onboardingStepIndex(ONBOARDING_FLOW.agency, step),
    });
  }

  private track(event: string, properties?: Record<string, unknown>): void {
    this.ga.trackOnboardingEvent(event, {
      flow: ONBOARDING_FLOW.agency,
      ...properties,
    });
  }

  private writeSession(draft: AgencyDraft): void {
    const storage = this.sessionStorage();
    if (!storage) {
      return;
    }
    try {
      storage.setItem(AGENCY_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // Ignore private-mode storage failures. The in-memory draft still works.
    }
  }

  private clearSession(): void {
    try {
      this.sessionStorage()?.removeItem(AGENCY_STORAGE_KEY);
    } catch {
      // Ignore private-mode storage failures.
    }
  }

  private sessionStorage(): Storage | undefined {
    return this.document.defaultView?.sessionStorage;
  }

  private hubspotToken(): string {
    const cookies = this.document.cookie.split(';');
    for (const raw of cookies) {
      const cookie = raw.trim();
      if (cookie.startsWith('hubspotutk=')) {
        return cookie.substring('hubspotutk='.length);
      }
    }
    return '';
  }

  private startCooldown(): void {
    this.clearCooldown();
    this.resendCooldown.set(60);
    this.cooldownTimer = setInterval(() => {
      const next = this.resendCooldown() - 1;
      if (next <= 0) {
        this.resendCooldown.set(0);
        this.clearCooldown();
        return;
      }
      this.resendCooldown.set(next);
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  private isExistingAccount(error: unknown): boolean {
    const code = (error as { code?: string }).code;
    if (code === 'auth/email-already-in-use') {
      return true;
    }
    if (error instanceof HttpErrorResponse && (error.status === 409 || error.status === 400)) {
      return this.readErrorMessage(error, '').toLowerCase().includes('already');
    }
    return false;
  }

  private readErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const message = (error.error as { message?: unknown } | null)?.message ?? fallback;
      if (Array.isArray(message)) {
        return String(message[0] ?? fallback);
      }
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }
}
